import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// Request interceptor to add auth token and start loading
apiClient.interceptors.request.use((config) => {
    // Import loading store dynamically to avoid circular dependency
    import('@/stores/loading').then(({ useLoadingStore }) => {
        const loadingStore = useLoadingStore()
        loadingStore.startLoading()
    })
    
    const token = localStorage.getItem('auth_token')
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// Response interceptor to handle auth errors and stop loading
apiClient.interceptors.response.use(
    (response) => {
        // Import loading store dynamically to avoid circular dependency
        import('@/stores/loading').then(({ useLoadingStore }) => {
            const loadingStore = useLoadingStore()
            loadingStore.stopLoading()
        })
        return response
    },
    (error) => {
        // Import loading store dynamically to avoid circular dependency
        import('@/stores/loading').then(({ useLoadingStore }) => {
            const loadingStore = useLoadingStore()
            loadingStore.stopLoading()
        })
        
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token')
            window.location.href = '/login'
        }
        return Promise.reject(error)
    }
)

// Game API functions
export const gameApi = {
    getGames: (year?: number) => 
        apiClient.get('/games', { params: year ? { y: year } : {} }),
    
    getRecentGames: (count = 5) => 
        apiClient.get('/games/recent', { params: { c: count } }),
    
    getGameDetails: (id: number) => 
        apiClient.get(`/games/${id}`),
    
    getOverview: (year?: number) => 
        apiClient.get('/games/overview', { params: year ? { y: year } : {} })
}

// User API functions
export const userApi = {
    getProfile: () => 
        apiClient.get('/users/me'),
    
    uploadAvatar: (file: File) => {
        const formData = new FormData()
        formData.append('avatar', file)
        return apiClient.post('/users/me/avatar', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    
    removeAvatar: () => 
        apiClient.delete('/users/me/avatar')
}

// Auth API functions
export const authApi = {
    login: (username: string, password: string) => 
        apiClient.post('/auth/login', { username, password }),
    
    logout: () => 
        apiClient.post('/auth/logout'),
    
    verifyToken: () => 
        apiClient.get('/auth/verify'),
    
    changePassword: (currentPassword: string, newPassword: string) => 
        apiClient.post('/auth/change-password', { currentPassword, newPassword })
}